import React, { useState } from 'react';
import { getAuth, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import cogoToast from 'cogo-toast';
import tw from 'twin.macro';
import styled from 'styled-components';

const Container = tw.div`bg-white p-6 rounded-lg shadow-md mt-6`;
const Title = tw.h3`text-xl font-bold text-gray-800 mb-4`;
const Form = tw.form`flex flex-col gap-4`;
const InputGroup = tw.div`flex flex-col`;
const Label = tw.label`text-sm font-semibold text-gray-600 mb-1`;
const Input = tw.input`p-2 border border-gray-300 rounded focus:outline-none focus:border-green-500`;
const Button = tw.button`bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition duration-200 disabled:opacity-50 w-full sm:w-auto`;

export default function ChangePassword() {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            cogoToast.error("New passwords do not match.");
            return;
        }

        if (newPassword.length < 6) {
            cogoToast.error("Password must be at least 6 characters.");
            return;
        }

        setLoading(true);
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            cogoToast.error("You must be logged in to change your password.");
            setLoading(false);
            return;
        }

        try {
            // Re-authenticate user
            const credential = EmailAuthProvider.credential(user.email, oldPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password
            await updatePassword(user, newPassword);
            cogoToast.success("Password updated successfully!");
            
            // Clear form
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            console.error(error);
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                cogoToast.error("Incorrect current password.");
            } else if (error.code === 'auth/too-many-requests') {
                cogoToast.error("Too many failed attempts. Try again later.");
            } else {
                cogoToast.error(`Failed to update password: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container>
            <Title>Change Password</Title>
            <Form onSubmit={handleSubmit}>
                <InputGroup>
                    <Label>Current Password</Label>
                    <Input 
                        type="password" 
                        value={oldPassword} 
                        onChange={e => setOldPassword(e.target.value)} 
                        required 
                    />
                </InputGroup>
                <InputGroup>
                    <Label>New Password</Label>
                    <Input 
                        type="password" 
                        value={newPassword} 
                        onChange={e => setNewPassword(e.target.value)} 
                        required 
                    />
                </InputGroup>
                <InputGroup>
                    <Label>Confirm New Password</Label>
                    <Input 
                        type="password" 
                        value={confirmPassword} 
                        onChange={e => setConfirmPassword(e.target.value)} 
                        required 
                    />
                </InputGroup>
                <div tw="mt-2">
                    <Button type="submit" disabled={loading}>
                        {loading ? 'Updating...' : 'Update Password'}
                    </Button>
                </div>
            </Form>
        </Container>
    );
}
